import { faMagnifyingGlass, faSquare, faSquareCheck, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Autocomplete, Chip, InputAdornment, styled, SxProps, TextField, Theme, Typography } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { device } from 'src/constants/mediaQueries';
import { productCategoryPathEnumerationSeparator } from 'src/utils/urls';

export interface OptionsWithLabelAndGroup {
  label: string;
  value: string;
  group?: string;
}

interface SearchableDropdownProps {
  placeholder?: string;
  options: string[] | OptionsWithLabelAndGroup[];
  sx?: SxProps<Theme>;
  value: string | OptionsWithLabelAndGroup[] | null;
  setValue: Dispatch<SetStateAction<any>>;
  startIcon?: IconDefinition;
  label?: string;
  fullWidth?: boolean;
  multiple?: boolean;
  loading?: boolean;
}

export default function SearchableDropdown({
  loading,
  placeholder,
  options,
  sx,
  value,
  setValue,
  startIcon,
  label,
  fullWidth,
  multiple,
}: Readonly<SearchableDropdownProps>) {
  return (
    <>
      <FieldLabel>
        {startIcon && <FieldIcon icon={startIcon} size="2x" />}
        {label}
      </FieldLabel>
      <SearchableDropdownList
        disabled={loading}
        sx={sx}
        fullWidth={fullWidth}
        multiple={multiple}
        disableClearable
        getOptionLabel={(option) =>
          typeof option === 'object' ? (option as OptionsWithLabelAndGroup).label : (option as string)
        }
        options={options}
        value={value}
        onChange={(_, newValue) => {
          if (typeof newValue === 'string') {
            setValue(newValue);
          } else {
            setValue(makeSelectionUnique(newValue as Record<string, string>[]));
          }
        }}
        groupBy={(option: any) => (option as OptionsWithLabelAndGroup).group ?? ''}
        renderGroup={(params) => {
          const depth = (params.group.match(/\./g) || []).length;
          const lastDot = params.group.lastIndexOf(productCategoryPathEnumerationSeparator);
          const parents = params.group
            .substring(lastDot, params.group.length)
            .replaceAll(productCategoryPathEnumerationSeparator, '');

          return (
            <li key={params.key}>
              {params.group && <CategoryGroupHeader depth={depth}>{parents}</CategoryGroupHeader>}
              <CategoryGroup depth={depth}>{params.children}</CategoryGroup>
            </li>
          );
        }}
        renderOption={(props: any, option: any, { selected }) => {
          const { key, ...otherProps } = props;
          const isTheOptionSelected =
            selected ||
            (typeof value !== 'string' &&
              value?.some(/* istanbul ignore next */ (entry) => entry.value === option.value));
          return (
            <div key={key} {...otherProps} data-testid="searchable-dropdown-option">
              <FontAwesomeIcon
                icon={isTheOptionSelected ? /* istanbul ignore next */ faSquareCheck : faSquare}
                style={{ marginRight: '1rem' }}
              />
              <Typography>
                {typeof option === 'object' ? (option as OptionsWithLabelAndGroup).label : (option as string)}
              </Typography>
            </div>
          );
        }}
        renderInput={(params) => (
          <SearchableTextField
            {...params}
            InputProps={
              !multiple
                ? {
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 3 }}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                      </InputAdornment>
                    ),
                  }
                : { ...params.InputProps }
            }
            placeholder={placeholder}
            variant="standard"
            data-testid="searchable-dropdown"
          />
        )}
        renderTags={(value: any, getTagProps) =>
          value.map((option: any, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                variant="outlined"
                label={
                  typeof option === 'object'
                    ? (option as OptionsWithLabelAndGroup).label
                    : /* istanbul ignore next */ option
                }
                key={key}
                {...tagProps}
              />
            );
          })
        }
      />
    </>
  );
}

const SearchableDropdownList = styled(Autocomplete)`
  & .MuiSvgIcon-root {
    background-color: ${({ theme }) => theme.palette.grey[900]};
    color: ${({ theme }) => theme.palette.primary.contrastText};
  }

  & .MuiChip-deleteIcon {
    background-color: transparent;
    color: ${({ theme }) => theme.palette.primary.dark};
  }

  & .MuiChip-root {
    color: ${({ theme }) => theme.palette.grey[400]}
    background-color: transparent;
    border: 1px ${({ theme }) => theme.palette.primary.dark} solid;
  }

  & .MuiFormControl-root {
    ${({ fullWidth }) => (fullWidth ? 'width: 100%;' : '')}
  }
`;

const SearchableTextField = styled(TextField)`
  width: 100%;
  max-width: 600px;
  background-color: white;
  border-radius: 5px;

  & .MuiAutocomplete-endAdornment {
    margin-right: 0.5rem;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 25rem;
  }

  input {
    height: 48px;
    padding: 0 1rem !important;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
    -webkit-text-fill-color: ${({ theme }) => theme.palette.grey[900]};
  }
`;

const FieldLabel = styled(Typography)`
  font-weight: bold;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: 1.25rem;

  @media ${device.mobileOrTablet} {
    width: 350px;
  }
  @media ${device.smallLaptop} {
    width: 350px;
    margin-bottom: 1rem;
  }
`;

const FieldIcon = styled(FontAwesomeIcon)`
  margin-right: 0.5rem;
`;

const CategoryGroupHeader = styled('div', { shouldForwardProp: (prop) => prop !== 'depth' })<{ depth: number }>`
  background-color: ${({ theme }) => theme.palette.grey[200]};
  padding: 0.25rem;
  padding-left: ${({ depth }) => 50 * depth || /* istanbul ignore next */ 10}px;
  font-weight: bold;
`;

const CategoryGroup = styled('div', { shouldForwardProp: (prop) => prop !== 'depth' })<{ depth: number }>`
  & .MuiAutocomplete-option {
    padding-left: ${({ depth }) => 50 * depth || 10}px;
  }
`;

function makeSelectionUnique(newValue: Record<string, string>[]) {
  const stringifiedUniqueValues = new Set(newValue.map((v) => JSON.stringify(v)));
  const uniqueListOfSelectedValues = Array.from(stringifiedUniqueValues).map((v) => JSON.parse(v));

  return uniqueListOfSelectedValues;
}
